import React from 'react';
import { DialogActions, Button, Box, Tooltip } from '@mui/material';

export interface ModalActionButtonsProps {
  /** Label for secondary action (Cancel, Back) */
  secondaryLabel: string;
  /** Label for primary CTA. Omit to show only secondary button. */
  primaryLabel?: string;
  onSecondary: () => void;
  onPrimary?: () => void;
  /** Disable primary button */
  primaryDisabled?: boolean;
  /** Tooltip shown when primary is disabled (wraps button in span so tooltip works) */
  primaryDisabledTooltip?: string;
  /** When true, render inside DialogActions (for modal footers). When false, render as Box (for inline in content). */
  asDialogActions?: boolean;
  /** Disable secondary button (e.g. while loading) */
  secondaryDisabled?: boolean;
}

/** Shared style for Cancel/Back buttons - use across import modals for consistency */
export const secondaryActionButtonSx = (theme: any) => ({
  textTransform: 'none',
  px: 2,
  borderRadius: '50px',
  color:
    theme.customColors?.themeNeutralSecondaryColor ||
    theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: 'action.hover'
  }
});

const secondaryButtonSx = secondaryActionButtonSx;

/** Shared style for primary CTA buttons (Save/Continue/etc.) across the app */
export const primaryActionButtonSx = (theme: any) => ({
  borderRadius: '50px',
  textTransform: 'none',
  px: 3,
  backgroundColor: theme.customColors?.themePrimary || 'primary.main',
  color: theme.customColors?.themeSecondary || 'white',
  '&:hover': {
    backgroundColor: theme.customColors?.themePrimary || 'primary.main',
    opacity: 0.9,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${theme.customColors?.themePrimary || 'primary.main'}40`
  },
  '&:disabled': {
    opacity: 0.5,
    backgroundColor: theme.customColors?.themePrimary || 'primary.main'
  },
  transition: 'all 0.2s ease'
});

const primaryButtonSx = primaryActionButtonSx;

const ModalActionButtons: React.FC<ModalActionButtonsProps> = ({
  secondaryLabel,
  primaryLabel,
  onSecondary,
  onPrimary,
  primaryDisabled = false,
  primaryDisabledTooltip,
  asDialogActions = true,
  secondaryDisabled = false
}) => {
  const primaryButton =
    primaryLabel && onPrimary ? (
      <Button
        variant="contained"
        onClick={onPrimary}
        disabled={primaryDisabled}
        sx={primaryButtonSx}
      >
        {primaryLabel}
      </Button>
    ) : null;

  const buttons = (
    <>
      <Button
        variant="text"
        onClick={onSecondary}
        disabled={secondaryDisabled}
        sx={secondaryButtonSx}
      >
        {secondaryLabel}
      </Button>
      {primaryButton && primaryDisabled && primaryDisabledTooltip ? (
        <Tooltip title={primaryDisabledTooltip} arrow placement="top">
          <span style={{ display: 'inline-flex' }}>{primaryButton}</span>
        </Tooltip>
      ) : (
        primaryButton
      )}
    </>
  );

  if (asDialogActions) {
    return (
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          gap: 1,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        {buttons}
      </DialogActions>
    );
  }

  return (
    <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
      {buttons}
    </Box>
  );
};

export default ModalActionButtons;
